import { Node } from 'contensis-delivery-api';
import clean from 'deep-cleaner';
import {
  AnalysedModels,
  ContentTypesResult,
  DependencyStatusByTarget,
  MigrateContentModelResponse,
  MigrateModelsPreview,
} from '~/models';
import { ContentTypeBuilder } from './ContentTypeBuilder';
import ModelMigrationService from '~/services/ModelMigrationService';
import { MigrateStatus } from '~/services';
import { sortByStatus } from '~/util/array';

export const mapMigrateResult = (
  service: ModelMigrationService,
  committed = false
): ContentTypesResult => {
  const { migrate, source, targets } = service;

  const mapDependencies = (c?: ContentTypeBuilder<any>) => {
    let dependencies: string[] = [];
    let dependencyOf: string[] = [];
    let defaults: string[] = [];
    let nodes: string[] = [];
    let tagGroups: string[] = [];

    if (c?.dependencies) {
      dependencies = [
        ...(c.dependencies.contentTypes || []),
        ...(c.dependencies.components || []),
      ];
      defaults = c.dependencies.defaults?.map((d) => d[0]);
      nodes = c.dependencies.nodes
        ?.map((d) => service.migrate.nodes.find((n) => n.id === d[0])?.path)
        .filter((p) => typeof p === 'string');
      tagGroups = c.dependencies.tagGroups;
    }

    if (c?.dependencyOf)
      dependencyOf = [
        ...(c.dependencyOf.contentTypes || []),
        ...(c.dependencyOf.components || []),
      ];

    return {
      dependencyOf: dependencyOf.sort(),
      dependencies: dependencies.sort(),
      defaults,
      nodes,
      tagGroups,
    };
  };

  // Map the response key `contentTypes`
  const contentTypes: AnalysedModels['contentTypes'] = Object.fromEntries(
    migrate.contentTypeIds.map((id) => {
      let dependencies = mapDependencies(); // initialise object with empty keys
      let dataFormat: string | undefined = undefined;
      const projects: DependencyStatusByTarget = {};
      for (const [projectId, repo] of Object.entries(targets)) {
        const c = repo.models.migrate.contentTypes.find(
          (c) => c.resource.id === id
        );
        if (c) {
          dataFormat = c.resource.dataFormat;
          // map dependencies from built content type
          dependencies = mapDependencies(c);
        }

        if (c?.status)
          projects[projectId] = {
            diff: c.diff,
            status: c.status,
            error: c.error?.message,
            versionNo: c.versionNo,
          };
      }
      return [
        id,
        {
          dataFormat,
          ...dependencies,
          projects,
        },
      ];
    })
  );

  // Map the response key `components`
  const components: AnalysedModels['components'] = Object.fromEntries(
    migrate.componentIds.map((id) => {
      let dependencies = mapDependencies(); // initialise object with empty keys
      const projects: DependencyStatusByTarget = {};
      for (const [projectId, repo] of Object.entries(targets)) {
        const c = repo.models.migrate.components.find(
          (c) => c.resource.id === id
        );

        if (c) dependencies = mapDependencies(c); // map dependencies from built content type

        if (c?.status)
          projects[projectId] = {
            diff: c.diff,
            status: c.status,
            error: c.error?.message,
            versionNo: c.versionNo,
          };
      }
      return [
        id,
        {
          ...dependencies,
          projects,
        },
      ];
    })
  );

  // Map the response key `defaults`
  const defaults: AnalysedModels['defaults'] = Object.fromEntries(
    migrate.entryIds.map((id) => {
      const projects: DependencyStatusByTarget = {};
      const dependencyOf: string[] = [];
      let entryTitle: string | undefined = undefined;
      let contentTypeId: string | undefined = undefined;
      const d = source.context.dependencies.getByContentId(id);
      if (d?.dependencyOf)
        dependencyOf.push(
          ...(d.dependencyOf.contentTypes || []),
          ...(d.dependencyOf.components || [])
        );
      for (const [projectId, repo] of Object.entries(targets)) {
        const me = repo.entries.migrate?.find((me) => me.originalId === id);
        entryTitle = me?.entryTitle;
        contentTypeId = me?.contentTypeId;
        if (me?.status)
          projects[projectId] = {
            id: me.id,
            diff: me.diff,
            error: me.error?.message,
            status: me.status,
          };
      }
      return [id, { entryTitle, contentTypeId, dependencyOf, projects }];
    })
  );

  // Map the response key `nodes`
  const nodePaths = new Set<string>();
  for (const repo of Object.values(targets)) {
    // don't add the built node if it is the tree root and is unchanged
    for (const mn of repo.nodes.migrateNodes)
      if (!(!mn.parentId && mn.status === 'no change')) nodePaths.add(mn.path);
  }

  const nodes: AnalysedModels['nodes'] = migrate.nodes.length
    ? Object.fromEntries(
        Array.from(nodePaths).map((path) => {
          const node = migrate.nodes.find((n) => n.path === path) as Node;
          const projects: DependencyStatusByTarget = {};
          const dependencyOf: string[] = [];
          let originalId: string | undefined = undefined;
          const d = source.context.dependencies.getByContentId(node?.id);
          if (d?.dependencyOf)
            dependencyOf.push(
              ...(d.dependencyOf.contentTypes || []),
              ...(d.dependencyOf.components || [])
            );

          for (const [projectId, repo] of Object.entries(targets)) {
            const mn = repo.nodes.migrateNodes?.find((mn) => mn.path === path);
            originalId = mn?.originalId;
            if (mn?.status)
              projects[projectId] = {
                id: mn.id,
                parentId: mn?.parentId,
                entryId: mn?.entryId,
                diff: mn.diff,
                status: mn.status,
                error: mn.error?.message,
              };
          }
          return [path, { id: originalId, dependencyOf, projects }];
        })
      )
    : {};

  // Map the response key `tagGroups`
  const tagGroups: AnalysedModels['tagGroups'] = Object.fromEntries(
    migrate.tagGroupIds.map((id) => {
      const dependencyOf: string[] = [];
      const projects: DependencyStatusByTarget = {};
      const d = source.context.dependencies.getByTagGroupId(id);
      if (d?.dependencyOf)
        dependencyOf.push(
          ...(d.dependencyOf.contentTypes || []),
          ...(d.dependencyOf.components || [])
        );
      for (const [projectId, repo] of Object.entries(targets)) {
        const tg = repo.tags.migrateGroups.find((g) => g.id === id);

        if (tg?.status)
          projects[projectId] = {
            diff: tg.diff,
            status: tg.status,
            error: tg.error?.message,
          };
      }
      return [
        id,
        {
          dependencyOf,
          projects,
        },
      ];
    })
  );

  // Re-map the above results by status
  // This drives the processing in the CommitMigration method

  // Create a distinct list of statuses and projects
  const projectIdList = new Set<string>();
  const statusList = new Set<MigrateStatus>();
  for (const c of [
    ...Object.values(contentTypes),
    ...Object.values(components),
    ...Object.values(tagGroups),
    ...Object.values(defaults),
    ...Object.values(nodes),
  ])
    for (const [projectId, p] of Object.entries(c.projects)) {
      projectIdList.add(projectId);
      statusList.add(p.status);
    }

  const projectResults = {} as MigrateModelsPreview['projects'];

  const getProjectResults = <T extends AnalysedModels[keyof AnalysedModels]>(
    projectId: string,
    resources: T
  ) => {
    return Object.entries(resources)
      .map(([id, c]: [string, AnalysedModels[keyof AnalysedModels][string]]) =>
        // if any resource includes this project, map it back with the response
        Object.entries(c.projects)
          .filter(([pId]) => pId === projectId)
          .map(([, p]) => {
            const result = {
              id: 'id' in c ? c.id || id : id, // we use the path as the id for nodes so we swap it back here
              entryId: 'entryId' in p ? p.entryId : undefined,
              entryTitle: 'entryTitle' in c ? c.entryTitle : undefined,
              contentTypeId: 'contentTypeId' in c ? c.contentTypeId : undefined,
              dataFormat: 'dataFormat' in c ? c.dataFormat : undefined,
              dependencyOf: c.dependencyOf,
              dependencies: 'dependencies' in c ? c.dependencies : undefined,
              parentId: 'parentId' in p ? p.parentId : undefined,
              path: 'id' in c ? id : undefined,
              status: p.status,
              diff: p.diff,
              error: p.error,
            };
            return result;
          })
      )
      .filter((r) => !!r)
      .flat();
  };

  for (const projectId of projectIdList) {
    // Get all resources of this status
    projectResults[projectId] = {
      contentTypes: getProjectResults(projectId, contentTypes),
      components: getProjectResults(projectId, components),
      tagGroups: getProjectResults(projectId, tagGroups),
      defaults: getProjectResults(projectId, defaults),
      nodes: getProjectResults(projectId, nodes),
    };
  }

  const statusResults = {} as MigrateModelsPreview['status'];

  const getStatusResults = <T extends AnalysedModels[keyof AnalysedModels]>(
    status: MigrateStatus,
    resources: T
  ) => {
    const result = Object.entries(resources)
      .map(([id, c]: [string, AnalysedModels[keyof AnalysedModels][string]]) =>
        // if any resource includes this status, map it back with the response
        Object.values(c.projects).some((p) => p.status === status)
          ? ({
              id: 'id' in c ? c.id || id : id, // we use the path as the id for nodes so we swap it back here
              entryTitle: 'entryTitle' in c ? c.entryTitle : undefined,
              contentTypeId: 'contentTypeId' in c ? c.contentTypeId : undefined,
              // map a list of projects that include the result at this status
              projectIds: Object.entries(c.projects)
                .map(([id, p]) => (p.status === status ? id : undefined))
                .filter((p): p is string => typeof p === 'string'),
              dataFormat: 'dataFormat' in c ? c.dataFormat : undefined,
              dependencyOf: c.dependencyOf,
              dependencies: 'dependencies' in c ? c.dependencies : undefined,
              path: 'id' in c ? id : undefined,
              status,
              transformedId: Object.entries(c.projects)
                .map(([id, p]) =>
                  p.status === status && 'id' in c && p.id !== c.id
                    ? p.id
                    : undefined
                )
                .filter(Boolean)
                .join(', '),
              // map the diffs to show as [projectId, diff]
              diff: Object.entries(c.projects)
                .map<[string, string] | undefined>(([id, p]) =>
                  p.status === status && p.diff ? [id, p.diff] : undefined
                )
                .filter((d): d is [string, string] => !!d),
              // map the errors to show as [projectId, error]
              errors: Object.entries(c.projects)
                .map<[string, string] | undefined>(([id, p]) =>
                  p.status === status && p.error ? [id, p.error] : undefined
                )
                .filter((d): d is [string, string] => !!d),
            } as MigrateContentModelResponse)
          : undefined
      )
      .filter(
        (r): r is MigrateContentModelResponse => typeof r !== 'undefined'
      );
    return result;
  };

  for (const status of [...statusList].sort(sortByStatus)) {
    // Get all resources of this status
    statusResults[status] = {
      contentTypes: getStatusResults(status, contentTypes),
      components: getStatusResults(status, components),
      tagGroups: getStatusResults(status, tagGroups),
      defaults: getStatusResults(status, defaults),
      nodes: getStatusResults(status, nodes),
    };
  }

  const query = {
    modelIds: service.payload.models?.map((m) =>
      typeof m === 'string' ? m : m.id
    ),
    contentTypeIds: service.payload.contentTypes,
    componentIds: service.payload.components,
  };

  // TODO: extend to include entry, node, tag group errors
  const errors =
    Object.keys(service.errors).length > 0 ? service.errors : undefined;

  const prepare = <T>(result: T) => {
    if (Object.keys(result || {}).length > 0) return clean(result);
    return undefined;
  };

  if (!committed) {
    const result: ContentTypesResult = {
      committed,
      query,
      errors,
      status: prepare(statusResults),
      projects: prepare(projectResults),
      contentTypes: prepare(contentTypes),
      components: prepare(components),
      tagGroups: prepare(tagGroups),
      defaults: prepare(defaults),
      nodes: prepare(nodes),
    };
    return result;
  } else {
    const result: ContentTypesResult = {
      committed,
      query,
      errors,
      modelsResult: service.migrateResult,
      status: prepare(statusResults),
    };
    return result;
  }
};
